import { css } from '@emotion/react'
import { memo, useCallback, useEffect, useState } from 'react'
import { DisplayUeopt, SettingsType } from '../../constants/enums'
import { capitalize } from '../../utilities/string'
import { useSettingSelector } from '../Hooks/useSettings'
import AccountBox from '../Icons/AccountBox'
import AccountDetailsCard from '../Icons/AccountDetailsCard'
import { GridDensityIcon } from '../Icons/GridDensity'
import Text from '../Icons/Text'
import VideoFull from '../Icons/VideoFull'
import { getThemeSpacing } from '../styles/appTheme'
import { palette } from '../styles/colors'
import shape from '../styles/shape'
import { PAPER_HEIGHTS } from '../theme'
import IconButton from './IconButton'

const rootStyles = css({
    position: 'absolute',
    bottom: getThemeSpacing(1),
    right: getThemeSpacing(1),
    borderRadius: shape.reallyBig,
    padding: `3px 6px`,
    background: palette.floatingToolbarBackground,
    zIndex: PAPER_HEIGHTS.floatingActionBar,
    display: 'flex',
})

const iconButtonStyles = css({
    position: 'relative',
    display: 'flex',
    marginLeft: 3,
    marginRight: 3,
    '&::after': {
        content: '""',
        display: 'inline-block',
        width: 1,
        height: 21,
        background: palette.floatingToolbarDivider,
        position: 'relative',
        top: 7,
        left: 3,
    },
    '&:last-child': {
        '&::after': {
            visibility: 'hidden',
        },
    },
    '& button': {
        padding: getThemeSpacing(0.625),
    },
})

const buttons = [
    { name: DisplayUeopt.default, icon: AccountBox, size: 0.9 },
    { name: DisplayUeopt.preview, icon: VideoFull, size: 1.1 },
    { name: DisplayUeopt.text, icon: Text, size: 1.1 },
    { name: DisplayUeopt.detailed, icon: AccountDetailsCard, size: 0.9 },
] as const

type ButtonDisplayTypeProps = {
    offset?: number
}

const ButtonDisplayType = memo((props: ButtonDisplayTypeProps) => {
    const { offset } = props

    const [gridDensity, saveGridDensity] = useSettingSelector(
        SettingsType.DEFAULT,
        'gridDensity',
    )
    const [display, saveDisplay] = useSettingSelector(
        SettingsType.DEFAULT,
        'display',
    )
    const [activeButton, setActiveButton] = useState(display)

    useEffect(() => {
        setActiveButton(display)
    }, [display])

    const onClickDisplay = useCallback(
        (name: DisplayUeopt) => () => {
            setActiveButton(name)
            saveDisplay(name)
        },
        [],
    )

    const onClickDensity = useCallback(() => {
        saveGridDensity(Math.max(1, (gridDensity + 1) % 4))
    }, [gridDensity])

    return (
        <div
            css={rootStyles}
            style={{ bottom: offset }}
            data-testid="Container__ButtonDisplayType"
            id="display-toolbar"
        >
            {buttons.map((button) => {
                const isActive = activeButton === button.name
                const ButtonIcon = button.icon

                return (
                    <div key={button.name} css={iconButtonStyles}>
                        {isActive && (
                            <IconButton
                                title={capitalize(button.name)}
                                onClick={onClickDensity}
                                // dim
                            >
                                <GridDensityIcon
                                    gridDensity={gridDensity}
                                    fill="#999"
                                />
                            </IconButton>
                        )}
                        {!isActive && (
                            <IconButton
                                title={capitalize(button.name)}
                                onClick={onClickDisplay(button.name)}
                            >
                                <ButtonIcon
                                    size={button.size || 1}
                                    color={palette.commonWhite}
                                    data-testid={`Button__${button.name}`}
                                />
                            </IconButton>
                        )}
                    </div>
                )
            })}
        </div>
    )
})

export default ButtonDisplayType
